import React from 'react';
import type { HorseCare } from '../../../../types/horseProfile';

interface CareEditFormProps {
  initialData: HorseCare;
  onSubmit: (data: HorseCare) => Promise<void>;
}

const DEFAULT_CARE_DATA: HorseCare = {
  grooming: {
    frequency: '',
    specialInstructions: '',
    products: []
  },
  shoeing: {
    frequency: '',
    lastDate: null,
    nextDate: null,
    farrier: '',
    specialNeeds: ''
  },
  turnout: {
    schedule: '',
    preferences: '',
    restrictions: ''
  },
  equipment: []
};

export const CareEditForm: React.FC<CareEditFormProps> = ({
  initialData = DEFAULT_CARE_DATA,
  onSubmit
}) => {
  const [formData, setFormData] = React.useState<HorseCare>({
    ...DEFAULT_CARE_DATA,
    ...initialData
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const addEquipment = () => {
    setFormData(prev => ({
      ...prev,
      equipment: [
        ...prev.equipment,
        { name: '', size: '', notes: '' }
      ]
    }));
  };

  const removeEquipment = (index: number) => {
    setFormData(prev => ({
      ...prev,
      equipment: prev.equipment.filter((_, i) => i !== index)
    }));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-4">Pflege</h4>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Häufigkeit
            </label>
            <input
              type="text"
              value={formData.grooming.frequency}
              onChange={(e) => setFormData({
                ...formData,
                grooming: { ...formData.grooming, frequency: e.target.value }
              })}
              className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Besondere Anweisungen
            </label>
            <textarea
              value={formData.grooming.specialInstructions}
              onChange={(e) => setFormData({
                ...formData,
                grooming: { ...formData.grooming, specialInstructions: e.target.value }
              })}
              rows={3}
              className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Pflegeprodukte
            </label>
            <input
              type="text"
              value={formData.grooming.products.join(', ')}
              onChange={(e) => setFormData({
                ...formData,
                grooming: {
                  ...formData.grooming,
                  products: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                }
              })}
              className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
              placeholder="Mit Komma getrennt eingeben"
            />
          </div>
        </div>
      </div>

      <div>
        <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-4">Hufpflege</h4>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Häufigkeit
            </label>
            <input
              type="text"
              value={formData.shoeing.frequency}
              onChange={(e) => setFormData({
                ...formData,
                shoeing: { ...formData.shoeing, frequency: e.target.value }
              })}
              className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Hufschmied
            </label>
            <input
              type="text"
              value={formData.shoeing.farrier}
              onChange={(e) => setFormData({
                ...formData,
                shoeing: { ...formData.shoeing, farrier: e.target.value }
              })}
              className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Letzter Termin
              </label>
              <input
                type="date"
                value={formData.shoeing.lastDate || ''}
                onChange={(e) => setFormData({
                  ...formData,
                  shoeing: { ...formData.shoeing, lastDate: e.target.value }
                })}
                className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Nächster Termin
              </label>
              <input
                type="date"
                value={formData.shoeing.nextDate || ''}
                onChange={(e) => setFormData({
                  ...formData,
                  shoeing: { ...formData.shoeing, nextDate: e.target.value }
                })}
                className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Besondere Anforderungen
            </label>
            <textarea
              value={formData.shoeing.specialNeeds}
              onChange={(e) => setFormData({
                ...formData,
                shoeing: { ...formData.shoeing, specialNeeds: e.target.value }
              })}
              rows={3}
              className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
            />
          </div>
        </div>
      </div>

      <div>
        <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-4">Koppelgang</h4>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Zeitplan
            </label>
            <input
              type="text"
              value={formData.turnout.schedule}
              onChange={(e) => setFormData({
                ...formData,
                turnout: { ...formData.turnout, schedule: e.target.value }
              })}
              className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Vorlieben
            </label>
            <textarea
              value={formData.turnout.preferences}
              onChange={(e) => setFormData({
                ...formData,
                turnout: { ...formData.turnout, preferences: e.target.value }
              })}
              rows={2}
              className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Einschränkungen
            </label>
            <textarea
              value={formData.turnout.restrictions}
              onChange={(e) => setFormData({
                ...formData,
                turnout: { ...formData.turnout, restrictions: e.target.value }
              })}
              rows={2}
              className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
            />
          </div>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-sm font-medium text-gray-900 dark:text-white">Ausrüstung</h4>
          <button
            type="button"
            onClick={addEquipment}
            className="px-3 py-1 text-sm text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300"
          >
            + Hinzufügen
          </button>
        </div>

        <div className="space-y-4">
          {formData.equipment.map((item, index) => (
            <div key={index} className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Bezeichnung
                  </label>
                  <input
                    type="text"
                    value={item.name}
                    onChange={(e) => {
                      const newEquipment = [...formData.equipment];
                      newEquipment[index] = { ...item, name: e.target.value };
                      setFormData({ ...formData, equipment: newEquipment });
                    }}
                    className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Größe
                  </label>
                  <input
                    type="text"
                    value={item.size}
                    onChange={(e) => {
                      const newEquipment = [...formData.equipment];
                      newEquipment[index] = { ...item, size: e.target.value };
                      setFormData({ ...formData, equipment: newEquipment });
                    }}
                    className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
                    required
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Anmerkungen
                  </label>
                  <textarea
                    value={item.notes}
                    onChange={(e) => {
                      const newEquipment = [...formData.equipment];
                      newEquipment[index] = { ...item, notes: e.target.value };
                      setFormData({ ...formData, equipment: newEquipment });
                    }}
                    rows={2}
                    className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
                  />
                </div>
              </div>

              <div className="mt-4 flex justify-end">
                <button
                  type="button"
                  onClick={() => removeEquipment(index)}
                  className="text-sm text-red-600 hover:text-red-500 dark:text-red-400 dark:hover:text-red-300"
                >
                  Entfernen
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </form>
  );
};